import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Search, Beaker } from 'lucide-react';
import { useRecipes } from '../../api/queries/recipe.queries';
import { useAuthStore } from '../../stores/auth.store';
import RecipeCard from './components/RecipeCard';
import RecipeEditModal from './components/RecipeEditModal';
import type { Recipe } from '../../api/queries/recipe.queries';

function RecipeManagementPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();
  const { data: recipes, refetch } = useRecipes();
  const [showModal, setShowModal] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<Recipe | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  const getLangValue = (multiLang: any) => {
    return multiLang?.[i18n.language as 'en' | 'th'] || multiLang?.en || '';
  };

  const handleCreate = () => {
    setEditingRecipe(undefined);
    setShowModal(true);
  };

  const handleEdit = (recipe: Recipe) => {
    setEditingRecipe(recipe);
    setShowModal(true);
  };

  const handleSuccess = () => {
    refetch();
  };

  // Filter recipes
  const filteredRecipes = recipes?.filter((recipe) => {
    // Status filter
    if (statusFilter && recipe.status !== statusFilter) return false;

    // Search filter
    if (searchQuery) {
      const menuItem = typeof recipe.menuItemId === 'object' ? recipe.menuItemId : null;
      const name = menuItem ? getLangValue(menuItem.name).toLowerCase() : '';
      const query = searchQuery.toLowerCase();
      if (!name.includes(query)) return false;
    }

    return true;
  });

  // Count by status
  const statusCounts = {
    all: recipes?.length || 0,
    published: recipes?.filter(r => r.status === 'published').length || 0,
    draft: recipes?.filter(r => r.status === 'draft').length || 0,
    review: recipes?.filter(r => r.status === 'review').length || 0,
    archived: recipes?.filter(r => r.status === 'archived').length || 0,
  };

  const canCreate = user?.role === 'HQ_ADMIN';

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="border-b bg-card px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Beaker className="h-6 w-6 text-primary" />
              {t('recipe.title')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{t('recipe.subtitle')}</p>
          </div>

          {canCreate && (
            <button
              onClick={handleCreate}
              className="rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2 shadow-sm"
            >
              <Plus className="h-4 w-4" />
              {t('recipe.createRecipe')}
            </button>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="border-b bg-card/50 px-6 py-3">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Status Filter Tabs */}
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            {[
              { value: '', label: 'All', count: statusCounts.all },
              { value: 'published', label: 'Published', count: statusCounts.published },
              { value: 'draft', label: 'Draft', count: statusCounts.draft },
              { value: 'review', label: 'Review', count: statusCounts.review },
            ].map((tab) => (
              <button
                key={tab.value}
                onClick={() => setStatusFilter(tab.value)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  statusFilter === tab.value
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.label}
                <span className={`ml-1.5 text-xs ${
                  statusFilter === tab.value ? 'text-primary' : 'text-muted-foreground'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Search - Right Side */}
          <div className="relative flex-1 max-w-md ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search recipes..."
              className="w-full rounded-lg border bg-background pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>
      </div>

      {/* Recipe Grid */}
      <div className="flex-1 overflow-y-auto p-6">
        {filteredRecipes && filteredRecipes.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
            {filteredRecipes.map((recipe) => (
              <RecipeCard
                key={recipe._id}
                recipe={recipe}
                onClick={() => handleEdit(recipe)}
                getLangValue={getLangValue}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <Beaker className="h-16 w-16 mb-4 opacity-20" />
            <p className="text-lg font-medium">No recipes found</p>
            <p className="text-sm">
              {searchQuery
                ? 'Try adjusting your search'
                : statusFilter
                ? `No ${statusFilter} recipes`
                : 'Create your first recipe to get started'}
            </p>
            {canCreate && !searchQuery && !statusFilter && (
              <button
                onClick={handleCreate}
                className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                Create Recipe
              </button>
            )}
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="border-t bg-card/50 px-6 py-3">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4 text-muted-foreground">
            <span>
              <strong className="text-foreground">{filteredRecipes?.length || 0}</strong> recipes
            </span>
            <span>•</span>
            <span>
              <strong className="text-foreground">
                {filteredRecipes?.reduce((acc, r) => acc + (r.formulas?.length || 0), 0) || 0}
              </strong> total formulas
            </span>
          </div>
          <div className="text-muted-foreground">
            {statusFilter && `Filtered by: ${statusFilter}`}
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showModal && (
        <RecipeEditModal
          recipe={editingRecipe}
          onClose={() => setShowModal(false)}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
}

export default RecipeManagementPage;
